import React, { useState } from "react";
import { FaCopy, FaDownload, FaEnvelopeOpenText, FaMagic, FaSpinner } from "react-icons/fa";
import { apiRequest } from "../../lib/api";
import "./AiTemplateDesigner.css";
import aidesigner from "../../assets/new/AI Email Template Generator for Responsive Email Campaigns mailsnappy.png";
import ManagedFaqSection, { pageFaqDefaults } from "../ManagedFaqSection/ManagedFaqSection";


const EXAMPLE_PROMPT =
  "Create a promotional email template for embroidery digitizing with blue theme and hero image.";

async function generateEmailTemplate(prompt) {
  const data = await apiRequest("/generate-email-template", {
    method: "POST",
    body: JSON.stringify({ prompt }),
  });

  return data.html || "";
}

function downloadHtml(html) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "ai-email-template.html";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export default function AiTemplateDesigner() {
  const [prompt, setPrompt] = useState("");
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copyStatus, setCopyStatus] = useState("");

  const handleGenerate = async (event) => {
    event.preventDefault();

    if (!prompt.trim()) {
      setError("Enter a prompt to generate an email template.");
      return;
    }

    setLoading(true);
    setError("");
    setCopyStatus("");

    try {
      const generatedHtml = await generateEmailTemplate(prompt.trim());
      if (!generatedHtml) throw new Error("The AI returned an empty template.");
      setHtml(generatedHtml);
    } catch (err) {
      setError(err.message || "Unable to generate template. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!html) return;

    try {
      await navigator.clipboard.writeText(html);
      setCopyStatus("Copied");
    } catch {
      setCopyStatus("Copy failed");
    }
  };

  const handleDownload = () => {
    if (!html) return;
    downloadHtml(html);
  };

  return (
    <main className="ai-designer-page">
      <section className="ai-designer-hero">
        <div className="ai-designer-copy">
          <span className="ai-designer-eyebrow">
            <FaMagic /> AI template designer
          </span>
          <h1>AI Email Template Generator for Responsive Email Campaigns</h1>
          <p>
            Turn your ideas into professional email campaigns with the MailSnappy AI Email Template Generator. Simply describe your campaign, promotion, audience, or brand style, and our AI creates responsive email templates with clean table based HTML that are ready to preview, customize, and use. Save time, maintain consistent branding, and create high quality email designs without coding or design experience.
          </p>
        </div>
        <div className="ai-designer-card" aria-label="Generated email preview sample">
                   <img src={aidesigner} alt="Email automation workflow preview" width="840" height="auto" />
        </div>
      </section>

      <section className="ai-designer-workspace">
        <form className="ai-designer-panel" onSubmit={handleGenerate}>
          <div className="ai-designer-panel-head">
            <FaEnvelopeOpenText />
            <div>
              <h2>Generate HTML email</h2>
              <p>Use one clear prompt. If you include an image URL, it will be used.</p>
            </div>
          </div>

          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder={EXAMPLE_PROMPT}
            rows={9}
            disabled={loading}
          />

          {error && <p className="ai-designer-error">{error}</p>}

          <button className="ai-designer-generate" type="submit" disabled={loading || !prompt.trim()}>
            {loading ? (
              <>
                <FaSpinner className="ai-designer-spin" /> Generating...
              </>
            ) : (
              <>
                <FaMagic /> Generate Template
              </>
            )}
          </button>

          <button className="ai-designer-copy-btn" type="button" onClick={handleCopy} disabled={!html}>
            <FaCopy /> Copy HTML
          </button>
          <button className="ai-designer-copy-btn" type="button" onClick={handleDownload} disabled={!html}>
            <FaDownload /> Download HTML
          </button>
          {copyStatus && <span className="ai-designer-copy-status">{copyStatus}</span>}
        </form>

        <div className="ai-designer-preview">
          <div className="ai-designer-preview-head">
            <h2>HTML email preview</h2>
            <span>{html ? "Generated" : "Waiting for prompt"}</span>
          </div>

          {html ? (
            <iframe title="AI generated email template preview" srcDoc={html} />
          ) : (
            <div className="ai-designer-empty">
              <FaEnvelopeOpenText />
              <p>Your generated responsive email template preview will appear here.</p>
            </div>
          )}
        </div>
      </section>
      <ManagedFaqSection
        pageKey="ai-template-designer"
        heading="AI designer FAQs"
        fallback={pageFaqDefaults["ai-template-designer"]}
      />

    </main>
  );
}
